'use client'

import { useSearchParams } from 'next/navigation'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useAuth } from '@/hooks/use-auth'
import { usePermissions } from '@/hooks/use-permissions'
import { useKnowledgeSourcesQuery } from '@/hooks/queries/knowledge-sources'
import { Button } from '@/components/ui/button'
import { KnowledgeSourcesPanel } from '@/components/knowledge-sources/knowledge-sources-panel'
import { GoogleIntegrationCard } from './google-integration-card'
import { CrmIntegrationCard } from '@/components/crm'
import { ShopifyIntegrationCard } from './shopify-integration-card'
import { DeliveryIntegrationCard } from '@/components/deliveries/delivery-integration-card'
import { MetaAdsIntegrationCard } from '@/components/integrations/meta-ads/meta-ads-integration-card'
import { useTranslations } from "next-intl"


export function IntegrationsPage() {
  const tUi = useTranslations("Integrations.IntegrationsIntegrationsPage")
  const tCommon = useTranslations('Common')

  const searchParams = useSearchParams()
  const { user, isLoading } = useAuth()
  const { can } = usePermissions()
  const knowledgeSources = useKnowledgeSourcesQuery()
  const role = user?.role
  const isAdmin = role === 'admin'
  const isSuperUser = role === 'super_user'
  const hasAccess = isAdmin || isSuperUser || can(['integrations.*'])

  // Check if user has an active tenant (either own tenant_id or impersonated)
  const hasActiveTenant = Boolean(user?.tenant_id || user?.impersonated_tenant_id)
  const requiresTenantSelection = !hasActiveTenant
  const openDeliveriesByDefault = searchParams.get('section') === 'deliveries'
  const openAdsPlatformsByDefault = searchParams.get('section') === 'ads-platforms'

  if (isLoading || (hasAccess && hasActiveTenant && knowledgeSources.isPending)) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div role="status" aria-label={tCommon('loading')} aria-busy="true" className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-48"></div>
            <div className="h-64 bg-gray-200 rounded w-full"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!hasAccess) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <Alert variant="destructive">
            <AlertTitle><span key={tUi("accessRestricted")}>{tUi("accessRestricted")}</span></AlertTitle>
            <AlertDescription>
              <span key={tUi("onlyTenantAdministratorsCanConfigureIntegrationsPleaseContact")}>{tUi("onlyTenantAdministratorsCanConfigureIntegrationsPleaseContact")}</span></AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  if (requiresTenantSelection) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <Alert>
            <AlertTitle><span key={tUi("selectATenant")}>{tUi("selectATenant")}</span></AlertTitle>
            <AlertDescription>
              <span key={isSuperUser
                ? tUi("chooseATenantFromTheSwitcherInThe")
                : tUi("yourUserAccountIsMissingTenantInformationPlease")}>{isSuperUser
                ? tUi("chooseATenantFromTheSwitcherInThe")
                : tUi("yourUserAccountIsMissingTenantInformationPlease")}</span>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  if (knowledgeSources.isError && !knowledgeSources.data) {
    return <div className="p-6"><div className="mx-auto max-w-4xl">
      <Alert variant="destructive"><AlertTitle><span>{tCommon('unknownError')}</span></AlertTitle>
        <AlertDescription><Button variant="outline" disabled={knowledgeSources.isFetching} aria-busy={knowledgeSources.isFetching}
          onClick={() => void knowledgeSources.refetch()}><span key={String(knowledgeSources.isFetching)}>{knowledgeSources.isFetching ? tCommon('loading') : tCommon('retry')}</span></Button></AlertDescription>
      </Alert>
    </div></div>
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground"><span key={tUi("integrations")}>{tUi("integrations")}</span></h1>
          <p className="text-muted-foreground mt-1"><span key={tUi("manageYourExternalServiceIntegrations")}>{tUi("manageYourExternalServiceIntegrations")}</span></p>
        </div>
        <GoogleIntegrationCard />
        <CrmIntegrationCard />
        <KnowledgeSourcesPanel entryPoint="integrations" />
        <section id="ads-platforms" className="scroll-mt-6">
          <MetaAdsIntegrationCard
            key={openAdsPlatformsByDefault ? 'ads-platforms-open' : 'ads-platforms-closed'}
            defaultOpen={openAdsPlatformsByDefault}
          />
        </section>
        <section id="delivery-integrations" className="scroll-mt-6">
          <DeliveryIntegrationCard
            key={openDeliveriesByDefault ? 'deliveries-open' : 'deliveries-closed'}
            defaultOpen={openDeliveriesByDefault}
          />
        </section>
        <ShopifyIntegrationCard />
      </div>
    </div>
  )
}
